import { z } from 'zod';
import rawCatalogue from './service-urls.json';

export * from './event-flows.js';
export * from './events.js';
export * from './lifecycles.js';

/**
 * The SquadLab OS deploy catalogue — the single source of truth for everything the fleet ships,
 * parsed from `service-urls.json` at the boundary (parse-don't-validate, ADR 0005). Register a new
 * thing by editing the JSON, never by hand-editing a consumer.
 *
 * Two registers, because a fleet ships two shapes of thing (ADR 0113):
 *  - `services` — URL-addressable, with a health endpoint the fleet probes. Consumed by the Command
 *    Center (UI tiles + fleet health probes); referenced by docs + the README.
 *  - `deployables` — shipped but not reachable over HTTP (Workers woken by cron, email or a binding).
 *    They have no URL and nothing to probe, so each row is pinned to the wrangler config that names
 *    the Worker at deploy time — the one machine source that can contradict it.
 */
export const ServiceEntrySchema = z.object({
  /** Stable service id (matches the `services/<id>` dir + the Command Center vessel id). */
  id: z.string().min(1),
  /** Human-readable name shown in the UI. */
  name: z.string().min(1),
  /** The canonical public URL (a `*.squadlab.ai` subdomain, except services not yet migrated). */
  url: z.string().url(),
  /** The health endpoint the fleet console probes. */
  health: z.string().url(),
  /** How the health endpoint answers: a JSON envelope, or a plain page (liveness only). */
  healthKind: z.enum(['json', 'page']).default('json'),
  /** `private` = behind Zero Trust Access; `public` = openly reachable (deliberate exception). */
  visibility: z.enum(['private', 'public']).default('private'),
  /** Whether the Command Center fleet health console should probe this service. */
  probe: z.boolean().default(true),
});

export type ServiceEntry = z.infer<typeof ServiceEntrySchema>;

/**
 * A deployable with no public URL. Deliberately NOT a loosened `ServiceEntry`: making `url`/`health`
 * optional would push "this might be missing" into every consumer that reads the service register.
 */
export const DeployableEntrySchema = z.object({
  /** Stable id, distinct from every service id. */
  id: z.string().min(1),
  /** Human-readable name shown wherever the fleet is listed. */
  name: z.string().min(1),
  /** What kind of thing ships. Only Workers today; the field exists so the next kind is additive. */
  kind: z.literal('worker'),
  /** What wakes it, since nothing calls it over a URL. */
  trigger: z.enum(['cron', 'email', 'binding', 'queue']),
  /** The deployed Cloudflare Worker name — its identity in the real world. */
  workerName: z.string().min(1),
  /** Repo-relative wrangler config that declares `workerName`; the row's verifier. */
  config: z.string().min(1),
  /** The guild or team that vouches for this row. */
  owner: z.string().min(1),
  /** Why it has no URL, and anything a reader would otherwise get wrong. */
  note: z.string().min(1),
});

export type DeployableEntry = z.infer<typeof DeployableEntrySchema>;

const CatalogueSchema = z.object({
  updated: z.string(),
  services: z.array(ServiceEntrySchema).min(1),
  deployables: z.array(DeployableEntrySchema).default([]),
});

const parsed = CatalogueSchema.parse(rawCatalogue);

/** Every service in the catalogue, in file order. */
export const SERVICE_CATALOGUE: readonly ServiceEntry[] = Object.freeze(parsed.services);

/** Every registered deployable with no public URL, in file order. */
export const DEPLOYABLE_CATALOGUE: readonly DeployableEntry[] = Object.freeze(parsed.deployables);

/** ISO date the catalogue was last updated. */
export const SERVICE_CATALOGUE_UPDATED: string = parsed.updated;

/** Look up a service by id (undefined if not catalogued). */
export function serviceById(id: string): ServiceEntry | undefined {
  return SERVICE_CATALOGUE.find((s) => s.id === id);
}

/** Look up a deployable by id (undefined if not catalogued). Never falls back to the service register. */
export function deployableById(id: string): DeployableEntry | undefined {
  return DEPLOYABLE_CATALOGUE.find((d) => d.id === id);
}

/** A service's canonical public URL by id (undefined if not catalogued). */
export function serviceUrl(id: string): string | undefined {
  return serviceById(id)?.url;
}

/** `{ id: url }` map — the shape the Command Center UI injects at build time. */
export function serviceUrlMap(): Record<string, string> {
  return Object.fromEntries(SERVICE_CATALOGUE.map((s) => [s.id, s.url]));
}
